<template>
  <div class="waw-number">
    <!-- ONE TEAMIO NUMBER PER SITE (Saumil, 2026-09-27). A site that already has a number never gets
         the picker: a second number is a conversation with us, not a button. The server refuses the
         purchase too (`already_has_number`); this is so nobody walks up to a refusal. -->
    <div v-if="hasExistingNumber" class="waw-noquote" data-test="waw-number-already-have">
      <p class="waw-noquote__lead">Your practice already has a Teamio number.</p>
      <p class="waw-noquote__body">
        Your Teamio number is <strong>{{ existingNumberLabel }}</strong>, so we can&rsquo;t set up
        another one here. If you need a second number, contact Teamio and we&rsquo;ll help.
      </p>
    </div>

    <!-- CANNOT SET ONE UP, DON'T OFFER THE FORM. Not a spinner and not a form that would fail on
         submit: when the server has just told us it cannot provision a number, the screen says so
         and points at the two things that can actually help. It says nothing about money — the
         practice is never shown a price, because Teamio pays for the number (2026-09-02). -->
    <div v-else-if="setupUnavailable" class="waw-noquote">
      <p class="waw-noquote__lead">We can&rsquo;t set up a number right now.</p>
      <p class="waw-noquote__body">
        Nothing about your account has changed. Use <strong>Refresh status</strong> below to try
        again. If it still doesn&rsquo;t work, contact Teamio and we&rsquo;ll sort it out with you.
      </p>
    </div>

    <template v-else>
      <!-- RECOMMENDED (2026-09-11). Chosen server-side from where the practice is; the first row is
           pre-selected, so the one-click path is simply "Get this number". -->
      <section class="waw-pick" data-test="waw-number-recommended">
        <p class="waw-pick__title">Recommended for your practice</p>
        <p v-if="loadingOptions" class="waw-pick__note">
          <b-spinner small class="mr-2" />Finding a number for you&hellip;
        </p>
        <template v-else-if="recommendedNumbers.length">
          <p v-if="reason" class="waw-pick__note" data-test="waw-number-reason">{{ reason }}</p>
          <PhoneNumberList
            v-model="selected"
            :numbers="recommendedNumbers"
            :disabled="busy"
            top-badge="Recommended"
            label="Recommended numbers"
          />
        </template>
        <p v-else-if="recommended && recommended.failed" class="waw-pick__note">
          We couldn&rsquo;t look up a suggestion just now. Search by area code below, or use
          <strong>Refresh status</strong> to try again.
        </p>
        <p v-else-if="recommended" class="waw-pick__note">
          We don&rsquo;t have a suggestion for your area. Search by area code below.
        </p>
      </section>

      <!-- THE PRACTICE'S OWN SEARCH — the same search box and list the A2P wizard uses. -->
      <section class="waw-pick" data-test="waw-number-search">
        <p class="waw-pick__title">Or search for a different number</p>
        <AreaCodeSearch
          label="Area code"
          :searching="searching"
          :disabled="busy"
          @search="$emit('search', $event)"
        />
        <template v-if="searchResult">
          <PhoneNumberList
            v-if="searchNumbers.length"
            v-model="selected"
            class="waw-pick__results"
            :numbers="searchNumbers"
            :disabled="busy"
            label="Numbers in that area code"
          />
          <p v-else class="waw-pick__note" data-test="waw-number-no-results">{{ emptyMessage }}</p>
        </template>
      </section>

      <p v-if="selected" class="waw-selected" data-test="waw-number-selected">
        You picked <strong>{{ selectedLabel }}</strong>.
      </p>

      <button
        type="button"
        class="waw-primary"
        :disabled="!canBuy"
        data-test="waw-number-buy"
        @click="onBuy"
      >
        <span v-if="busy"><b-spinner small class="mr-2" />Working&hellip;</span>
        <span v-else>Get this number</span>
      </button>
    </template>

    <!-- The local-number choice, offered HERE because this is the first screen where we know the
         country and whether a local number exists there. See WizardTriageStep's header. -->
    <div v-if="localAvailable && !hasExistingNumber" class="waw-alt">
      <p class="waw-alt__text">
        A local number in your own country is also possible. Our team sets those up by hand rather
        than through this page.
      </p>
      <button
        type="button"
        class="waw-secondary"
        :disabled="busy"
        data-test="waw-number-prefer-local"
        @click="$emit('navigate', 'handoff')"
      >
        I&rsquo;d rather have a local number
      </button>
    </div>
  </div>
</template>

<script>
/**
 * Wizard screen — the one that gets the practice a number.
 *
 * ── WHAT THIS SCREEN MAY AND MAY NOT DO ──────────────────────────────────────────────────────────
 * It may take a number, ask for confirmation, and emit an intent. It does not make the request: the
 * shell owns axios, the error banner and the status refresh, exactly as it does for triage, so there
 * is one place that knows what a failed setup should put on screen and one busy flag disabling Back
 * and Refresh while the request is in flight.
 *
 * ── NO PRICE. ANYWHERE. (2026-09-02 — Saumil's decision) ─────────────────────────────────────────
 * TEAMIO PAYS FOR THE NUMBER, so this screen states no price, asks for no agreement to one, and
 * sends none. What used to be here — a price panel, a "Buy this number for $1.15/month" button, and
 * an `acceptedPrice` echoed back to the server — was ported from the A2P wizard by mistake, against
 * the standing rule that Twilio number prices are never customer-facing. The server still price-
 * checks the purchase, INTERNALLY, against its own quote and a ceiling; none of that is this
 * component's business and none of it reaches the browser.
 *
 * So: do not add a price prop, a price line, or a currency here. If a cost ever becomes something we
 * charge a practice for, that is a product decision to re-take with Saumil — not a field to restore.
 *
 * ── WHEN WE CANNOT SET ONE UP, THERE IS NO FORM ──────────────────────────────────────────────────
 * `setupUnavailable` comes from the SHELL, and only after the server has actually refused (its
 * `quote_unavailable` / `no_price_quote` codes — both meaning "we could not provision a number just
 * now"). The practice then gets an explanation and a Refresh instead of a form that would fail again
 * on submit. Before any attempt the form is offered normally: the browser has no way to know the
 * server's answer in advance, and a speculative refusal would block a setup that would have worked.
 *
 * ── THE CONFIRM IS A GATE, NOT A COURTESY ────────────────────────────────────────────────────────
 * `onBuy` emits NOTHING until `msgBoxConfirm` has resolved true. Money is no longer the reason — the
 * ONE-WAY-NESS is: this number becomes the practice's WhatsApp identity and cannot be swapped out
 * once it is in use. That is worth a yes/no on its own.
 *
 * ── PICK FROM A LIST, NOT A TEXT BOX (2026-09-11) ────────────────────────────────────────────────
 * This screen used to be one free-text box ("Our team chooses this number with you and enters it
 * here"). It now shows `my-number-options`: a RECOMMENDED list chosen from where the practice is,
 * with the first number pre-selected, and an area-code SEARCH. Both lists are the shared
 * components/phone-numbers pieces the A2P wizard also uses — one picker, two screens — but the
 * PURCHASE stays this wizard's own (`my-purchase-number` → the WhatsApp purchase, never A2P's).
 *
 * Only a number the screen is SHOWING can be bought (`isOffered`): a stale pick from an earlier
 * search falls back to the recommendation instead of staying purchasable while off screen.
 */
import {
  normalizePhoneNumber,
  isValidPhoneNumber,
  purchaseConfirmMessage,
} from '../wizardActions';
import { recommendationReason, isOffered, searchEmptyMessage } from '../numberChoice';
import PhoneNumberList from '../../phone-numbers/PhoneNumberList.vue';
import AreaCodeSearch from '../../phone-numbers/AreaCodeSearch.vue';
import { formatUsNumber, numberPlace } from '../../phone-numbers/phoneNumbers';

export default {
  name: 'WizardNumberStep',

  components: { PhoneNumberList, AreaCodeSearch },

  props: {
    /**
     * The server has told us it cannot provision a number right now. Set by the shell from the
     * refusal code — never guessed at locally, and never derived from a price (there isn't one).
     */
    setupUnavailable: { type: Boolean, default: false },
    /**
     * The site's Teamio number (my-status `whatsappNumber` = GENERAL_PHONE), or '' when it has none.
     * Non-blank ⇒ no picker and no purchase: one Teamio number per site (2026-09-27).
     */
    existingNumber: { type: String, default: '' },
    /** From the narrowed country gate — whether a local number is sold in this country. */
    localAvailable: { type: Boolean, default: false },
    /** A request is in flight somewhere in the wizard. Every control here goes dead. */
    busy: { type: Boolean, default: false },
    /**
     * The recommendation — a my-number-options body `{ basis, areaCode, region, numbers }` — or
     * null before it has loaded. `failed: true` when the shell could not load it.
     */
    recommended: { type: Object, default: null },
    /** The last area-code search, same shape, or null before one has run. */
    searchResult: { type: Object, default: null },
    /** The recommendation is loading. */
    loadingOptions: { type: Boolean, default: false },
    /** An area-code search is in flight. */
    searching: { type: Boolean, default: false },
  },

  data() {
    return {
      /** The picked E.164 number, from either list. */
      selected: '',
      /** The confirm dialog is open. Separate from `busy`: no request exists yet. */
      confirming: false,
    };
  },

  computed: {
    recommendedNumbers() {
      return (this.recommended && Array.isArray(this.recommended.numbers)) ? this.recommended.numbers : [];
    },

    searchNumbers() {
      return (this.searchResult && Array.isArray(this.searchResult.numbers)) ? this.searchResult.numbers : [];
    },

    reason() {
      return recommendationReason(this.recommended);
    },

    emptyMessage() {
      return searchEmptyMessage(this.searchResult && this.searchResult.areaCode);
    },

    selectedLabel() {
      const n = [...this.recommendedNumbers, ...this.searchNumbers].find((x) => x.phoneNumber === this.selected);
      const place = numberPlace(n);
      return place ? `${formatUsNumber(this.selected)} in ${place}` : formatUsNumber(this.selected);
    },

    /** A well-formed number that one of the two lists is actually showing. */
    numberIsValid() {
      return isValidPhoneNumber(this.selected) && isOffered(this.selected, this.recommended, this.searchResult);
    },

    /** Blank = no number, the same rule the server uses. */
    hasExistingNumber() {
      return String(this.existingNumber || '').trim() !== '';
    },

    existingNumberLabel() {
      return formatUsNumber(String(this.existingNumber || '').trim());
    },

    /** A number we are willing to send, and nothing else in flight. */
    canBuy() {
      return !this.busy && !this.confirming && !this.setupUnavailable && !this.hasExistingNumber && this.numberIsValid;
    },
  },

  watch: {
    recommended: { immediate: true, handler() { this.keepSelectionOffered(); } },
    searchResult() { this.keepSelectionOffered(); },
  },

  methods: {
    /**
     * Keep the pick on a number that is on screen. A still-offered pick is left alone (a new search
     * does not steal it); otherwise the recommendation's first number, or nothing.
     */
    keepSelectionOffered() {
      if (this.selected && isOffered(this.selected, this.recommended, this.searchResult)) return;
      this.selected = (this.recommendedNumbers[0] && this.recommendedNumbers[0].phoneNumber) || '';
    },

    /**
     * Confirm first, emit second. The order is the guard.
     *
     * The dialog restates the number and the fact that it cannot be swapped out later, then
     * `purchase` is emitted only for an explicit yes — a cancel, a dismiss and a backdrop click all
     * resolve falsy and leave this screen exactly as it was.
     */
    async onBuy() {
      if (!this.canBuy) return;
      const phoneNumber = normalizePhoneNumber(this.selected);
      if (!phoneNumber) return;

      this.confirming = true;
      let confirmed = false;
      try {
        confirmed = await this.$bvModal.msgBoxConfirm(
          purchaseConfirmMessage(phoneNumber),
          {
            title: 'Confirm this number',
            okVariant: 'primary',
            okTitle: 'Yes, use this number',
            cancelTitle: 'Cancel',
            centered: true,
          }
        );
      } finally {
        this.confirming = false;
      }
      if (!confirmed) return;

      // NO acceptedPrice. The shell POSTs { siteID, idToken, phoneNumber } and nothing else.
      this.$emit('purchase', { phoneNumber });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../PatientPayments/tokens';

.waw-noquote {
  padding: 0.9rem 1rem;
  border-radius: $pp-radius-sm;
  background: $pp-amber-subtle;
  border: 1px solid $pp-amber-border;
  max-width: 640px;
}

.waw-noquote__lead {
  margin: 0 0 0.4rem;
  color: $pp-amber-text;
  font-size: 0.9rem;
  font-weight: 700;
  line-height: 1.6;
}

.waw-noquote__body {
  margin: 0;
  color: $pp-text-muted;
  font-size: 0.85rem;
  line-height: 1.6;

  strong { color: $pp-text-strong; }
}

/* The `.waw-price*` rules were REMOVED on 2026-09-02 with the price panel they styled. Teamio pays
   for the number, so there is no price element on this screen to style. */

/* The free-text box's `.waw-field*` / `.waw-input` / `.waw-operator-note` rules were REMOVED on
   2026-09-11 with the box: the number is now picked from the shared list (components/phone-numbers),
   which carries its own styles. */
.waw-pick {
  max-width: 640px;
  margin: 0 0 1.4rem;
}

.waw-pick__title {
  margin: 0 0 0.4rem;
  font-size: 0.9rem;
  font-weight: 700;
  color: $pp-text-strong;
}

.waw-pick__note {
  display: flex;
  align-items: center;
  margin: 0 0 0.6rem;
  font-size: 0.83rem;
  line-height: 1.55;
  color: $pp-text-muted;

  strong { color: $pp-text-strong; }
}

.waw-pick__results {
  margin-top: 0.8rem;
}

.waw-selected {
  margin: 0 0 0.8rem;
  font-size: 0.88rem;
  color: $pp-text-muted;

  strong { color: $pp-text-strong; }
}

/* Styled here rather than through b-button so the rules stay inside this component's scope. */
.waw-primary {
  display: inline-flex;
  align-items: center;
  padding: 0.55rem 1.1rem;
  font-size: 0.9rem;
  font-weight: 700;
  color: #fff;
  background: $pp-brand;
  border: 1px solid $pp-brand;
  border-radius: $pp-radius-sm;
  cursor: pointer;

  &:hover:not(:disabled),
  &:focus:not(:disabled) {
    background: $pp-brand-deep;
    border-color: $pp-brand-deep;
  }

  &:disabled {
    cursor: default;
    opacity: 0.55;
  }
}

.waw-alt {
  max-width: 640px;
  margin: 1.6rem 0 0;
  padding-top: 1.1rem;
  border-top: 1px solid $pp-border;
}

.waw-alt__text {
  margin: 0 0 0.6rem;
  font-size: 0.83rem;
  line-height: 1.6;
  color: $pp-text-muted;
}

.waw-secondary {
  display: inline-flex;
  align-items: center;
  padding: 0.45rem 0.9rem;
  font-size: 0.85rem;
  font-weight: 700;
  color: $pp-brand;
  background: $pp-surface;
  border: 1px solid $pp-border-strong;
  border-radius: $pp-radius-sm;
  cursor: pointer;

  &:hover:not(:disabled),
  &:focus:not(:disabled) {
    border-color: $pp-brand;
    background: $pp-brand-subtle;
  }

  &:disabled {
    cursor: default;
    opacity: 0.55;
  }
}
</style>
